export const M9_UNIT_13 = {
  id: "m9-u13-giving-advice",
  title: "Unidad 13: Giving Advice with Emotions",
  topic: "Master giving advice naturally! Learn If I were you and question phrases to help friends with their problems. Combine emotions vocabulary to give thoughtful, caring advice in English!",
  iconType: "lightbulb",
  grammarImage: "/grammar/grammar-m9-u13.jpg",
  grammarText: "Use If I were you + would to give advice (even with I/he/she!). Use How about/What about + -ing for suggestions. Have you tried/thought of + -ing to propose solutions. Perfect for helping friends!",
  
  sets: [
    {
      id: "m9-u13-s1",
      title: "Set 1: Emotions Vocabulary - Negative Feelings 😟",
      context: `**Friend's Confession**

Sarah: "I'm feeling really stressed about my exams. I'm so worried I might fail!"`,
      
      questions: [
        {
          type: "mc",
          q: "Sarah is feeling:",
          options: [
            "Stressed and worried",
            "Happy and excited",
            "Angry and upset"
          ],
          a: "Stressed and worried",
          tip: "Look for the two emotion words she uses!",
          successtip: "✓ NEGATIVE EMOTIONS!\nStressed and worried often go together! Common feelings before exams! 📚😰"
        },
        {
          type: "match",
          q: "Match the emotions with their meanings:",
          pairs: [
            { left: "Worried", right: "Thinking something bad might happen" },
            { left: "Stressed", right: "Feeling pressure and tension" },
            { left: "Anxious", right: "Nervous about something uncertain" }
          ],
          a: "completed",
          successtip: "✓ EMOTION NUANCES!\nThese three are similar but subtly different! Native speakers use all three! 🧠"
        },
        {
          type: "input",
          q: "Complete: I'm _____ about my job interview tomorrow. (nervous feeling)",
          a: "anxious",
          alts: ["worried", "nervous", "stressed"],
          tip: "Any nervous emotion word works here!",
          successtip: "✓ FLEXIBLE EMOTIONS!\nAll these words work! English has many ways to express the same feeling! 💭"
        },
        {
          type: "order",
          q: "Traduce: Estoy muy estresado por el trabajo",
          words: ["I'm", "very", "stressed", "about", "work"],
          a: "I'm very stressed about work",
          extra: ["of", "for"],
          tip: "Stressed ABOUT something! Not of or for!",
          successtip: "✓ PREPOSITION!\nStressed ABOUT is the natural pattern! Remember this combo! 🎯"
        },
        {
          type: "mc",
          q: "Which emotion means annoyed because things aren't working?",
          options: [
            "Frustrated",
            "Relieved",
            "Delighted"
          ],
          a: "Frustrated",
          successtip: "✓ FRUSTRATION!\nSuper common feeling! When your computer doesn't work, you feel frustrated! 💻😤"
        },
        {
          type: "order",
          q: "Traduce: Ella está preocupada por su familia",
          words: ["She's", "worried", "about", "her", "family"],
          a: "She's worried about her family",
          extra: ["of"],
          successtip: "✓ WORRIED ABOUT!\nSame pattern: WORRIED ABOUT (not of)! 👨‍👩‍👧‍👦"
        },
        {
          type: "mc",
          q: "If someone is upset, they are:",
          options: [
            "Unhappy or hurt emotionally",
            "Very happy",
            "Extremely tired"
          ],
          a: "Unhappy or hurt emotionally",
          tip: "Upset means emotionally hurt, not physically!",
          successtip: "✓ UPSET!\nCan mean sad, angry, or disappointed! Multi-purpose emotion word! 😔"
        },
        {
          type: "input",
          q: "Complete: I feel _____ because I have too much to do. (pressure feeling)",
          a: "stressed",
          alts: ["overwhelmed", "anxious"],
          successtip: "✓ OVERWHELMED!\nGreat advanced word! Means too much to handle! 🌊"
        },
        {
          type: "match",
          q: "Match the situation with the emotion:",
          pairs: [
            { left: "Lost your keys", right: "Frustrated" },
            { left: "Big exam tomorrow", right: "Anxious" },
            { left: "Friend said something mean", right: "Upset" }
          ],
          a: "completed",
          successtip: "✓ REAL SITUATIONS!\nThese emotions match perfectly with these situations! Natural combinations! ✅"
        },
        {
          type: "order",
          q: "Traduce: Estoy frustrado con mi teléfono",
          words: ["I'm", "frustrated", "with", "my", "phone"],
          a: "I'm frustrated with my phone",
          extra: ["about", "of"],
          tip: "Frustrated WITH things! Frustrated ABOUT situations!",
          successtip: "✓ PREPOSITION CHOICE!\nWITH for people/things! ABOUT for situations! Both work but with is more common! 📱"
        }
      ]
    },
    
    {
      id: "m9-u13-s2",
      title: "Set 2: Emotions Vocabulary - Positive Feelings 😊",
      context: `**Good News!**

Mike: "The exam is cancelled! I'm so relieved! I wasn't ready at all!"`,
      
      questions: [
        {
          type: "mc",
          q: "Mike feels relieved because:",
          options: [
            "The exam is cancelled and he wasn't ready",
            "He passed the exam",
            "He studied really hard"
          ],
          a: "The exam is cancelled and he wasn't ready",
          successtip: "✓ RELIEVED!\nRelieved = when something bad DOESN'T happen! Stress goes away! 😅"
        },
        {
          type: "match",
          q: "Match the positive emotions:",
          pairs: [
            { left: "Relieved", right: "When worry goes away" },
            { left: "Delighted", right: "Very happy about something" },
            { left: "Grateful", right: "Thankful for something" }
          ],
          a: "completed",
          successtip: "✓ POSITIVE EMOTIONS!\nThese show appreciation and happiness! Use them to sound more sophisticated! 🌟"
        },
        {
          type: "input",
          q: "Complete: I'm _____ you could help me! (thankful feeling)",
          a: "grateful",
          alts: ["thankful"],
          tip: "Formal way to say thank you as a feeling!",
          successtip: "✓ GRATEFUL!\nGrateful is more formal than thankful! Perfect for professional settings! 🙏"
        },
        {
          type: "order",
          q: "Traduce: Estoy encantada con mi nuevo trabajo",
          words: ["I'm", "delighted", "with", "my", "new", "job"],
          a: "I'm delighted with my new job",
          extra: ["about"],
          successtip: "✓ DELIGHTED!\nDelighted is stronger than happy! Shows you're REALLY happy! 🎉"
        },
        {
          type: "mc",
          q: "If you're content, you feel:",
          options: [
            "Satisfied and peaceful",
            "Very excited",
            "Angry and upset"
          ],
          a: "Satisfied and peaceful",
          successtip: "✓ CONTENT!\nCalm happiness! Not jumping-with-joy, just peacefully happy! 😌"
        },
        {
          type: "order",
          q: "Traduce: Me siento aliviado",
          words: ["I", "feel", "relieved"],
          a: "I feel relieved",
          extra: ["am", "happy"],
          tip: "You can say I'm relieved or I feel relieved - both work!",
          successtip: "✓ TWO PATTERNS!\nI'm [emotion] OR I feel [emotion] - completely interchangeable! 🔄"
        },
        {
          type: "input",
          q: "Complete: She's _____ to be home after her long trip. (happy to be back)",
          a: "relieved",
          alts: ["happy", "glad", "delighted"],
          successtip: "✓ FLEXIBLE!\nMany positive emotions work! Relieved suggests the trip was tiring! ✈️"
        },
        {
          type: "mc",
          q: "Which word means extremely happy?",
          options: [
            "Delighted",
            "Worried",
            "Frustrated"
          ],
          a: "Delighted",
          successtip: "✓ INTENSITY!\nDelighted > pleased > happy > content (from strongest to calmest)! 📊"
        },
        {
          type: "match",
          q: "Match emotions with their intensity:",
          pairs: [
            { left: "Content", right: "Calm happiness" },
            { left: "Happy", right: "Normal happiness" },
            { left: "Delighted", right: "Strong happiness" }
          ],
          a: "completed",
          successtip: "✓ EMOTION SCALE!\nEnglish has many words for the same basic feeling! Choose based on intensity! 🎚️"
        },
        {
          type: "order",
          q: "Traduce: Estoy agradecido por tu ayuda",
          words: ["I'm", "grateful", "for", "your", "help"],
          a: "I'm grateful for your help",
          extra: ["with", "about"],
          tip: "Grateful FOR something! Not with or about!",
          successtip: "✓ GRATEFUL FOR!\nAlways use FOR with grateful! Fixed pattern! 🔒"
        }
      ]
    },
    
    {
      id: "m9-u13-s3",
      title: "Set 3: Basic Advice with If I were you 💡",
      context: `**Helping a Friend**

Lisa: "I don't know what to do about my noisy neighbors."
You: "If I were you, I would talk to them politely first."`,
      
      questions: [
        {
          type: "mc",
          q: "What's the advice pattern used?",
          options: [
            "If I were you, I would...",
            "You must...",
            "You have to..."
          ],
          a: "If I were you, I would...",
          tip: "This is the gentle advice pattern!",
          successtip: "✓ IF I WERE YOU!\nSounds caring and respectful! Not bossy like You must! 🤝"
        },
        {
          type: "input",
          q: "Complete: If I _____ you, I would study more. (advice word)",
          a: "were",
          tip: "Always WERE for all subjects! Even I, he, she!",
          successtip: "✓ WERE FOR ALL!\nGrammar rule: If I were, If he were, If she were - ALWAYS were! 📚"
        },
        {
          type: "order",
          q: "Traduce: Si yo fuera tú, descansaría",
          words: ["If", "I", "were", "you,", "I", "would", "rest"],
          a: "If I were you, I would rest",
          extra: ["was", "should"],
          tip: "Pattern: If I were you, I would + verb!",
          successtip: "✓ ADVICE PATTERN!\nThis pattern works for ANY advice! Just change the verb! 🔄"
        },
        {
          type: "mc",
          q: "Why do we say If I WERE you (not was)?",
          options: [
            "It's a special grammar rule for giving advice",
            "It's a mistake",
            "Only British English uses were"
          ],
          a: "It's a special grammar rule for giving advice",
          successtip: "✓ SUBJUNCTIVE!\nThis is called subjunctive mood - fancy term, but just remember: always WERE! 🎓"
        },
        {
          type: "order",
          q: "Traduce: Si yo fuera tú, hablaría con él",
          words: ["If", "I", "were", "you,", "I", "would", "talk", "to", "him"],
          a: "If I were you, I would talk to him",
          extra: ["was"],
          successtip: "✓ PERFECT ADVICE!\nComma after you in writing! Natural pause in speech! ✍️"
        },
        {
          type: "match",
          q: "Match the advice with the situation:",
          pairs: [
            { left: "Feeling sick", right: "If I were you, I'd see a doctor" },
            { left: "Lost keys", right: "If I were you, I'd retrace my steps" },
            { left: "Stressed about work", right: "If I were you, I'd take a break" }
          ],
          a: "completed",
          successtip: "✓ NATURAL ADVICE!\nAll use I'd = I would in natural speech! Natives contract! 🗣️"
        },
        {
          type: "input",
          q: "Complete: If I were you, I _____ apologize. (advice word)",
          a: "would",
          alts: ["'d"],
          tip: "Would or the contraction 'd both work!",
          successtip: "✓ CONTRACTION!\nI'd sounds more natural in conversation! I would is more formal! 🎯"
        },
        {
          type: "mc",
          q: "Which is correct?",
          options: [
            "If I were you, I would call her",
            "If I was you, I would call her",
            "If I were you, I will call her"
          ],
          a: "If I were you, I would call her",
          tip: "Remember: WERE (not was) + WOULD (not will)!",
          successtip: "✓ BOTH PARTS!\nWere in the first part, would in the second! Don't mix them up! ⚠️"
        },
        {
          type: "order",
          q: "Traduce: Si yo fuera tú, buscaría otro trabajo",
          words: ["If", "I", "were", "you,", "I'd", "look", "for", "another", "job"],
          a: "If I were you, I'd look for another job",
          extra: ["was", "will"],
          successtip: "✓ NATURAL CONTRACTION!\nI'd sounds friendly! Using full I would can sound too formal! 💼"
        },
        {
          type: "input",
          q: "What comes after If I were you, I would?",
          a: "verb",
          alts: ["base form", "infinitive"],
          tip: "The action word! Like go, talk, study!",
          successtip: "✓ BASE FORM!\nAlways base form (not -ing, not -ed)! If I were you, I'd GO (not going)! ✅"
        }
      ]
    },
    
    {
      id: "m9-u13-s4",
      title: "Set 4: How about + -ing Suggestions 🤔",
      context: `**Making Plans**

Tom: "I'm bored. What should we do?"
You: "How about going to the movies?"`,
      
      questions: [
        {
          type: "mc",
          q: "What form comes after How about?",
          options: [
            "-ing form (going)",
            "Base form (go)",
            "To + verb (to go)"
          ],
          a: "-ing form (going)",
          tip: "Always -ing after How about!",
          successtip: "✓ HOW ABOUT + ING!\nFixed pattern! How about going, How about eating, How about trying! 📝"
        },
        {
          type: "order",
          q: "Traduce: ¿Qué tal si vamos al parque?",
          words: ["How", "about", "going", "to", "the", "park?"],
          a: "How about going to the park?",
          extra: ["go", "we"],
          tip: "How about + -ing! Don't use go or we!",
          successtip: "✓ QUESTION FORM!\nHow about is already asking! No need for extra question words! ❓"
        },
        {
          type: "input",
          q: "Complete: How about _____ pizza tonight? (order)",
          a: "ordering",
          alts: ["getting", "having"],
          tip: "Add -ing to the verb!",
          successtip: "✓ FLEXIBLE!\nOrdering, getting, or having all work! English loves options! 🍕"
        },
        {
          type: "mc",
          q: "How about taking a break? means:",
          options: [
            "I suggest we take a break",
            "You must take a break",
            "Are you taking a break?"
          ],
          a: "I suggest we take a break",
          successtip: "✓ GENTLE SUGGESTION!\nNot a command! It's a friendly proposal! 🤝"
        },
        {
          type: "order",
          q: "Traduce: ¿Qué tal si cocinamos juntos?",
          words: ["How", "about", "cooking", "together?"],
          a: "How about cooking together?",
          extra: ["cook", "to"],
          successtip: "✓ SIMPLE PATTERN!\nShorter than What if we cook - more natural! 👨‍🍳"
        },
        {
          type: "match",
          q: "Match the suggestion with the situation:",
          pairs: [
            { left: "Friend is tired", right: "How about resting?" },
            { left: "It's hot", right: "How about swimming?" },
            { left: "We're hungry", right: "How about eating?" }
          ],
          a: "completed",
          successtip: "✓ NATURAL SUGGESTIONS!\nPerfect for informal situations with friends! 👥"
        },
        {
          type: "input",
          q: "Complete: How about _____ a movie tonight? (watch)",
          a: "watching",
          tip: "Watch + -ing!",
          successtip: "✓ WATCHING!\nDouble the consonant before -ing! Watch → watching, run → running! 🎬"
        },
        {
          type: "mc",
          q: "Which is correct?",
          options: [
            "How about meeting at 6?",
            "How about to meet at 6?",
            "How about meet at 6?"
          ],
          a: "How about meeting at 6?",
          tip: "Only -ing works after How about!",
          successtip: "✓ ONLY -ING!\nNever to or base form! Common mistake! ⚠️"
        },
        {
          type: "order",
          q: "Traduce: ¿Qué tal si intentamos este restaurante?",
          words: ["How", "about", "trying", "this", "restaurant?"],
          a: "How about trying this restaurant?",
          extra: ["try", "we"],
          successtip: "✓ TRYING NEW THINGS!\nHow about trying is super common for suggestions! 🍽️"
        },
        {
          type: "input",
          q: "Transform to suggestion: We could go shopping → How about _____?",
          a: "going shopping",
          alts: ["shopping"],
          tip: "Convert the verb to -ing form!",
          successtip: "✓ SAME MEANING!\nHow about = We could = Let's - all suggestions! Different styles! 🛍️"
        }
      ]
    },
    
    {
      id: "m9-u13-s5",
      title: "Set 5: What about + -ing Suggestions 💭",
      context: `**Problem Solving**

Alex: "My car broke down. I don't know how to get to work."
You: "What about taking the bus?"`,
      
      questions: [
        {
          type: "mc",
          q: "How about and What about are:",
          options: [
            "Exactly the same meaning",
            "Completely different",
            "Only for questions"
          ],
          a: "Exactly the same meaning",
          successtip: "✓ INTERCHANGEABLE!\nHow about = What about - use either! Both are casual and friendly! 🔄"
        },
        {
          type: "order",
          q: "Traduce: ¿Qué tal si llamamos a María?",
          words: ["What", "about", "calling", "Maria?"],
          a: "What about calling Maria?",
          extra: ["call", "to"],
          tip: "Same pattern as How about: + -ing!",
          successtip: "✓ WHAT ABOUT!\nCompletely interchangeable with How about! Pick your favorite! 📞"
        },
        {
          type: "input",
          q: "Complete: What about _____ for a walk? (go)",
          a: "going",
          tip: "Go + -ing = going!",
          successtip: "✓ GOING!\nGoing for a walk is a fixed expression! Common suggestion! 🚶‍♂️"
        },
        {
          type: "mc",
          q: "Which is correct?",
          options: [
            "What about asking for help?",
            "What about to ask for help?",
            "What about ask for help?"
          ],
          a: "What about asking for help?",
          tip: "Always -ing after What about!",
          successtip: "✓ SAME RULE!\nSame as How about - always -ing! No exceptions! ✅"
        },
        {
          type: "order",
          q: "Traduce: ¿Qué tal si tomamos un taxi?",
          words: ["What", "about", "taking", "a", "taxi?"],
          a: "What about taking a taxi?",
          extra: ["take", "to"],
          successtip: "✓ TAKING A TAXI!\nFixed expression! Also catching a taxi or getting a taxi! 🚕"
        },
        {
          type: "match",
          q: "Match identical suggestions:",
          pairs: [
            { left: "How about leaving now?", right: "What about leaving now?" },
            { left: "How about studying together?", right: "What about studying together?" },
            { left: "How about trying this?", right: "What about trying this?" }
          ],
          a: "completed",
          successtip: "✓ PERFECT MATCH!\nYou can swap them in ANY sentence! Total flexibility! 🎯"
        },
        {
          type: "input",
          q: "Complete: What about _____ something different? (try)",
          a: "trying",
          tip: "Try + -ing!",
          successtip: "✓ TRYING SOMETHING!\nTrying something + adjective is super common! Something new, something fun! 🆕"
        },
        {
          type: "mc",
          q: "You can use What about for:",
          options: [
            "Suggestions and alternatives",
            "Only questions",
            "Only commands"
          ],
          a: "Suggestions and alternatives",
          successtip: "✓ VERSATILE!\nUse it for suggesting plans, giving advice, proposing solutions! Multi-purpose! 🔧"
        },
        {
          type: "order",
          q: "Traduce: ¿Qué tal si nos reunimos mañana?",
          words: ["What", "about", "meeting", "tomorrow?"],
          a: "What about meeting tomorrow?",
          extra: ["meet", "we"],
          successtip: "✓ MEETING!\nDouble the t! Meet → meeting! Important spelling rule! 📅"
        },
        {
          type: "input",
          q: "Change to What about: Let's have lunch together → What about _____?",
          a: "having lunch together",
          alts: ["having lunch", "lunch together"],
          tip: "Convert have to -ing form!",
          successtip: "✓ CONVERSION!\nLet's + verb → What about + -ing! Easy transformation! 🍱"
        }
      ]
    },
    
    {
      id: "m9-u13-s6",
      title: "Set 6: Have you tried + -ing 🔍",
      context: `**Tech Support**

Customer: "My phone won't charge!"
Support: "Have you tried restarting it? That usually fixes the problem."`,
      
      questions: [
        {
          type: "mc",
          q: "Have you tried + -ing is used to:",
          options: [
            "Suggest something that might solve a problem",
            "Ask about past actions only",
            "Give commands"
          ],
          a: "Suggest something that might solve a problem",
          tip: "It's a gentle way to propose solutions!",
          successtip: "✓ PROBLEM-SOLVING!\nPerfect for helping someone fix something! Sounds experienced! 🔧"
        },
        {
          type: "order",
          q: "Traduce: ¿Has intentado apagarlo y encenderlo?",
          words: ["Have", "you", "tried", "turning", "it", "off", "and", "on?"],
          a: "Have you tried turning it off and on?",
          extra: ["turn", "to"],
          tip: "Have you tried + -ing!",
          successtip: "✓ TECH ADVICE!\nClassic tech support advice! Turn it off and on again fixes everything! 💻"
        },
        {
          type: "input",
          q: "Complete: Have you tried _____ to customer service? (talk)",
          a: "talking",
          alts: ["speaking", "calling"],
          tip: "Talk + -ing = talking!",
          successtip: "✓ FLEXIBLE!\nTalking, speaking, or calling all work! Different but similar! 📞"
        },
        {
          type: "mc",
          q: "Which is correct?",
          options: [
            "Have you tried asking for help?",
            "Have you tried to ask for help?",
            "Have you tried ask for help?"
          ],
          a: "Have you tried asking for help?",
          tip: "Only -ing works after Have you tried!",
          successtip: "✓ ASKING!\nHave you tried asking suggests doing it NOW if you haven't! ❓"
        },
        {
          type: "order",
          q: "Traduce: ¿Has intentado beber más agua?",
          words: ["Have", "you", "tried", "drinking", "more", "water?"],
          a: "Have you tried drinking more water?",
          extra: ["drink", "to"],
          successtip: "✓ HEALTH ADVICE!\nCommon advice for headaches, fatigue, etc.! 💧"
        },
        {
          type: "match",
          q: "Match the problem with the advice:",
          pairs: [
            { left: "Can't sleep", right: "Have you tried reading before bed?" },
            { left: "Back pain", right: "Have you tried stretching?" },
            { left: "Stressed", right: "Have you tried meditating?" }
          ],
          a: "completed",
          successtip: "✓ COMMON SOLUTIONS!\nThese are typical advice patterns! Natives use these all the time! 🧘"
        },
        {
          type: "input",
          q: "Complete: Have you tried _____ it in rice? (put - for wet phone)",
          a: "putting",
          tip: "Put + -ing = putting (double the t)!",
          successtip: "✓ PHONE RESCUE!\nOld trick for wet phones! Put + t + ing = putting! 📱💧"
        },
        {
          type: "mc",
          q: "Have you tried suggests:",
          options: [
            "Maybe you haven't done it yet",
            "You definitely did it",
            "You should never do it"
          ],
          a: "Maybe you haven't done it yet",
          successtip: "✓ GENTLE SUGGESTION!\nAsks if they've done it, but also suggests doing it! Smart pattern! 💡"
        },
        {
          type: "order",
          q: "Traduce: ¿Has intentado usar una aplicación?",
          words: ["Have", "you", "tried", "using", "an", "app?"],
          a: "Have you tried using an app?",
          extra: ["use", "to"],
          successtip: "✓ APP SUGGESTION!\nUsing an app is modern advice! For meditation, exercise, language learning! 📱"
        },
        {
          type: "input",
          q: "Complete: Have you tried _____ help from a professional? (get)",
          a: "getting",
          alts: ["seeking", "asking for"],
          tip: "Get + -ing = getting (double the t)!",
          successtip: "✓ SERIOUS ADVICE!\nGetting help suggests seeing a doctor, therapist, lawyer, etc.! 👨‍⚕️"
        }
      ]
    },
    
    {
      id: "m9-u13-s7",
      title: "Set 7: Have you thought of + -ing 💡",
      context: `**Career Advice**

Mentor: "You're not happy at your job? Have you thought of looking for something new?"`,
      
      questions: [
        {
          type: "mc",
          q: "Have you thought of is similar to:",
          options: [
            "Have you considered",
            "You must",
            "Did you do"
          ],
          a: "Have you considered",
          tip: "Thought of = considered = thinking about an idea!",
          successtip: "✓ SYNONYMS!\nHave you thought of = Have you considered - exact same meaning! 🧠"
        },
        {
          type: "order",
          q: "Traduce: ¿Has pensado en cambiar de carrera?",
          words: ["Have", "you", "thought", "of", "changing", "careers?"],
          a: "Have you thought of changing careers?",
          extra: ["change", "to"],
          tip: "Have you thought of + -ing!",
          successtip: "✓ CAREER ADVICE!\nBig life advice! More serious than Have you tried! 💼"
        },
        {
          type: "input",
          q: "Complete: Have you thought of _____ to a different city? (move)",
          a: "moving",
          tip: "Move + -ing = moving!",
          successtip: "✓ MOVING!\nMoving to + place is a common life change suggestion! 🏠"
        },
        {
          type: "mc",
          q: "Have you thought of vs Have you tried:",
          options: [
            "Thought of = considering an idea, tried = actually doing it",
            "They're exactly the same",
            "Tried is for future, thought of is for past"
          ],
          a: "Thought of = considering an idea, tried = actually doing it",
          tip: "Thought of is about thinking! Tried is about action!",
          successtip: "✓ SUBTLE DIFFERENCE!\nThought of = have you considered? Tried = have you done? 🤔 vs 🔨"
        },
        {
          type: "order",
          q: "Traduce: ¿Has pensado en tomar clases?",
          words: ["Have", "you", "thought", "of", "taking", "classes?"],
          a: "Have you thought of taking classes?",
          extra: ["take", "to"],
          successtip: "✓ LEARNING ADVICE!\nTaking classes to improve skills! Common professional advice! 🎓"
        },
        {
          type: "match",
          q: "Match the situation with advice:",
          pairs: [
            { left: "Unhappy job", right: "Have you thought of quitting?" },
            { left: "Want better health", right: "Have you thought of joining a gym?" },
            { left: "Financial problems", right: "Have you thought of budgeting?" }
          ],
          a: "completed",
          successtip: "✓ LIFE ADVICE!\nAll big decisions! Have you thought of is for important choices! 🎯"
        },
        {
          type: "input",
          q: "Complete: Have you thought of _____ your own business? (start)",
          a: "starting",
          tip: "Start + -ing = starting!",
          successtip: "✓ ENTREPRENEUR ADVICE!\nStarting a business = becoming your own boss! Big life change! 🚀"
        },
        {
          type: "mc",
          q: "Which is correct?",
          options: [
            "Have you thought of traveling?",
            "Have you thought of to travel?",
            "Have you thought of travel?"
          ],
          a: "Have you thought of traveling?",
          tip: "Always -ing after thought of!",
          successtip: "✓ SAME PATTERN!\nSame as all other suggestion phrases: always -ing! ✈️"
        },
        {
          type: "order",
          q: "Traduce: ¿Has pensado en hablar con un consejero?",
          words: ["Have", "you", "thought", "of", "talking", "to", "a", "counselor?"],
          a: "Have you thought of talking to a counselor?",
          extra: ["talk", "speaking"],
          successtip: "✓ PROFESSIONAL HELP!\nCounselor = therapist/advisor! Suggests getting expert help! 👨‍⚕️"
        },
        {
          type: "input",
          q: "Complete: Have you thought of _____ back to school? (go)",
          a: "going",
          alts: ["returning"],
          tip: "Go + -ing = going!",
          successtip: "✓ EDUCATION ADVICE!\nGoing back to school for more education! Common at any age! 🎓"
        }
      ]
    },
    
    {
      id: "m9-u13-s8",
      title: "Set 8: Combining Emotions with Advice 🎯",
      context: `**Friend's Problem**

Jenna: "I'm so stressed and worried about my presentation tomorrow!"
You: "If I were you, I'd practice one more time. Have you tried recording yourself?"`,
      
      questions: [
        {
          type: "mc",
          q: "What emotion is Jenna feeling?",
          options: [
            "Stressed and worried",
            "Happy and excited",
            "Angry and frustrated"
          ],
          a: "Stressed and worried",
          tip: "She mentions both emotions!",
          successtip: "✓ COMBINED EMOTIONS!\nPeople often feel multiple emotions at once! Natural to mention both! 😰😟"
        },
        {
          type: "order",
          q: "Traduce: Si yo fuera tú, practicaría más",
          words: ["If", "I", "were", "you,", "I'd", "practice", "more"],
          a: "If I were you, I'd practice more",
          extra: ["was", "will"],
          tip: "Advice pattern: If I were you, I'd + verb!",
          successtip: "✓ PRESENTATION ADVICE!\nPracticing reduces stress! Good practical advice! 🎤"
        },
        {
          type: "input",
          q: "Complete: I'm feeling _____ about tomorrow. (worried + anxious feeling)",
          a: "anxious",
          alts: ["worried", "nervous", "stressed"],
          tip: "Any nervous emotion works!",
          successtip: "✓ ABOUT FUTURE!\nAnxious/worried ABOUT for future events! Anxious/worried FOR for people! 📅"
        },
        {
          type: "mc",
          q: "What advice pattern is used: Have you tried recording yourself?",
          options: [
            "Have you tried + -ing",
            "If I were you",
            "How about + -ing"
          ],
          a: "Have you tried + -ing",
          successtip: "✓ TRYING NEW METHODS!\nHave you tried suggests a specific solution! 🎥"
        },
        {
          type: "order",
          q: "Traduce: ¿Qué tal si tomas un descanso?",
          words: ["How", "about", "taking", "a", "break?"],
          a: "How about taking a break?",
          extra: ["take", "you"],
          successtip: "✓ STRESS RELIEF!\nTaking a break is common advice for stressed people! ☕"
        },
        {
          type: "match",
          q: "Match emotions with appropriate advice:",
          pairs: [
            { left: "Stressed", right: "How about relaxing?" },
            { left: "Worried", right: "Have you tried talking to someone?" },
            { left: "Frustrated", right: "What about taking a different approach?" }
          ],
          a: "completed",
          successtip: "✓ MATCHING ADVICE!\nGood advice addresses the specific emotion! Empathy matters! 💙"
        },
        {
          type: "input",
          q: "Complete: If I were you, I _____ take the day off. (advice word)",
          a: "would",
          alts: ["'d"],
          tip: "Would or 'd both work!",
          successtip: "✓ MENTAL HEALTH!\nTaking a day off for mental health is important advice! 🏖️"
        },
        {
          type: "mc",
          q: "Someone says I'm so frustrated with this project! Best response:",
          options: [
            "Have you thought of asking for help?",
            "You're stupid",
            "I don't care"
          ],
          a: "Have you thought of asking for help?",
          tip: "Empathetic advice is best!",
          successtip: "✓ EMPATHY!\nGood friends offer solutions, not criticism! 🤝"
        },
        {
          type: "order",
          q: "Traduce: ¿Has pensado en hablar con tu jefe?",
          words: ["Have", "you", "thought", "of", "talking", "to", "your", "boss?"],
          a: "Have you thought of talking to your boss?",
          extra: ["talk", "speaking"],
          successtip: "✓ WORKPLACE ADVICE!\nDirect communication often solves work problems! 💼"
        },
        {
          type: "input",
          q: "Complete: I feel _____ now that the exam is over! (relief emotion)",
          a: "relieved",
          tip: "When worry goes away, you feel...?",
          successtip: "✓ RELIEVED!\nStress → Relief! Natural emotional progression! 😅"
        }
      ]
    },
    
    {
      id: "m9-u13-s9",
      title: "Set 9: Natural Conversations - Multiple Advice 💬",
      context: `**Real Support**

Sam: "I'm so anxious about my job interview!"
You: "If I were you, I'd prepare answers to common questions. Have you tried practicing with a friend? What about researching the company?"`,
      
      questions: [
        {
          type: "mc",
          q: "How many pieces of advice are given?",
          options: [
            "Three",
            "One",
            "Two"
          ],
          a: "Three",
          tip: "Count: If I were you, Have you tried, What about!",
          successtip: "✓ MULTIPLE ADVICE!\nNatives often give several suggestions! More helpful! 💡💡💡"
        },
        {
          type: "order",
          q: "Traduce: Si yo fuera tú, llegaría temprano",
          words: ["If", "I", "were", "you,", "I'd", "arrive", "early"],
          a: "If I were you, I'd arrive early",
          extra: ["was", "will"],
          tip: "If I were you, I'd + verb!",
          successtip: "✓ INTERVIEW TIP!\nArriving early shows you're responsible! Classic advice! ⏰"
        },
        {
          type: "input",
          q: "Complete: What about _____ professional clothes? (wear)",
          a: "wearing",
          tip: "Wear + -ing = wearing!",
          successtip: "✓ DRESS CODE!\nWearing professional clothes = dress appropriately! Important first impression! 👔"
        },
        {
          type: "mc",
          q: "Which gives the strongest advice?",
          options: [
            "If I were you, I would...",
            "How about...?",
            "Have you tried...?"
          ],
          a: "If I were you, I would...",
          tip: "If I were you is more direct than questions!",
          successtip: "✓ ADVICE STRENGTH!\nStrongest → Weakest: If I were you > You should > How about > Have you tried 📊"
        },
        {
          type: "order",
          q: "Traduce: ¿Has intentado investigar sobre la empresa?",
          words: ["Have", "you", "tried", "researching", "the", "company?"],
          a: "Have you tried researching the company?",
          extra: ["research", "to"],
          successtip: "✓ PREPARATION!\nResearching the company shows interest! Interviewers love this! 🔍"
        },
        {
          type: "match",
          q: "Match the advice type with example:",
          pairs: [
            { left: "Direct advice", right: "If I were you, I'd prepare" },
            { left: "Gentle suggestion", right: "How about practicing?" },
            { left: "Question advice", right: "Have you thought of asking for help?" }
          ],
          a: "completed",
          successtip: "✓ ADVICE STYLES!\nMix them in conversation! Sounds more natural and less bossy! 🎭"
        },
        {
          type: "input",
          q: "Complete: Have you thought of _____ questions to ask them? (prepare)",
          a: "preparing",
          tip: "Prepare + -ing = preparing!",
          successtip: "✓ INTERVIEW HACK!\nAsking good questions impresses interviewers! Shows you're serious! ❓"
        },
        {
          type: "mc",
          q: "I'm grateful you're helping me - What emotion?",
          options: [
            "Grateful (thankful)",
            "Worried",
            "Frustrated"
          ],
          a: "Grateful (thankful)",
          successtip: "✓ GRATITUDE!\nShowing gratitude when someone helps you! Good manners! 🙏"
        },
        {
          type: "order",
          q: "Traduce: ¿Qué tal si llevas copias de tu currículum?",
          words: ["What", "about", "bringing", "copies", "of", "your", "resume?"],
          a: "What about bringing copies of your resume?",
          extra: ["bring", "to"],
          successtip: "✓ PROFESSIONAL TIP!\nExtra copies show you're prepared! Practical advice! 📄"
        },
        {
          type: "input",
          q: "Complete: If I were you, I'd _____ confident body language. (use)",
          a: "use",
          alts: ["show", "have"],
          tip: "Base form after would!",
          successtip: "✓ BODY LANGUAGE!\nNon-verbal communication matters! Stand tall, make eye contact! 💪"
        }
      ]
    },
    
    {
      id: "m9-u13-s10",
      title: "Set 10: Advanced Integration & Responses 🌟",
      context: `**Complete Conversation**

Emily: "I'm feeling so stressed and overwhelmed. I don't know what to do."
You: "I understand. If I were you, I'd make a list of priorities. Have you thought of delegating some tasks? What about taking a mental health day? Remember, it's okay to ask for help when you're feeling this way."`,
      
      questions: [
        {
          type: "mc",
          q: "What's the first step in this advice?",
          options: [
            "Show empathy first (I understand)",
            "Give immediate solutions",
            "Ask questions"
          ],
          a: "Show empathy first (I understand)",
          tip: "Always acknowledge emotions before giving advice!",
          successtip: "✓ EMPATHY FIRST!\nBest friends listen before advising! I understand opens the conversation! 💙"
        },
        {
          type: "order",
          q: "Traduce: Entiendo cómo te sientes",
          words: ["I", "understand", "how", "you", "feel"],
          a: "I understand how you feel",
          extra: ["what", "that"],
          tip: "How you feel not what you feel!",
          successtip: "✓ EMPATHY STATEMENT!\nAlways start with empathy! Shows you care! Essential in English! 🤗"
        },
        {
          type: "input",
          q: "Complete: Have you thought of _____ some tasks to others? (give to others)",
          a: "delegating",
          alts: ["assigning", "giving"],
          tip: "Delegating = giving tasks to others!",
          successtip: "✓ DELEGATING!\nFancy word! Means giving tasks to team members! Professional vocabulary! 📋"
        },
        {
          type: "mc",
          q: "Taking a mental health day means:",
          options: [
            "Taking a day off to rest your mind",
            "Going to a doctor",
            "Working from home"
          ],
          a: "Taking a day off to rest your mind",
          successtip: "✓ MENTAL HEALTH!\nModern concept! Taking care of mental health is as important as physical! 🧠💚"
        },
        {
          type: "order",
          q: "Traduce: Está bien pedir ayuda",
          words: ["It's", "okay", "to", "ask", "for", "help"],
          a: "It's okay to ask for help",
          extra: ["good", "fine"],
          tip: "It's okay to + verb is reassuring!",
          successtip: "✓ PERMISSION PHRASE!\nIt's okay to gives permission! Reduces guilt! Caring language! 💚"
        },
        {
          type: "match",
          q: "Match the emotion with empathy response:",
          pairs: [
            { left: "Stressed", right: "I can see you're under pressure" },
            { left: "Worried", right: "I understand you're concerned" },
            { left: "Frustrated", right: "I know it's annoying" }
          ],
          a: "completed",
          successtip: "✓ EMPATHY VOCABULARY!\nThese phrases show you're listening! Key to good advice! 👂"
        },
        {
          type: "input",
          q: "Complete: What about _____ priorities first? (make a list)",
          a: "listing",
          alts: ["prioritizing", "organizing"],
          tip: "Listing or prioritizing both work!",
          successtip: "✓ ORGANIZATION!\nListing priorities helps when overwhelmed! Practical advice! 📝"
        },
        {
          type: "mc",
          q: "Which shows you understand before giving advice?",
          options: [
            "I understand. If I were you...",
            "Just do this:",
            "You should stop complaining"
          ],
          a: "I understand. If I were you...",
          tip: "Empathy + advice is the best combo!",
          successtip: "✓ STRUCTURE!\nEmpathy statement → Advice! Perfect support pattern! Natural English! 🎯"
        },
        {
          type: "order",
          q: "Traduce: ¿Has pensado en hablar con alguien profesional?",
          words: ["Have", "you", "thought", "of", "talking", "to", "a", "professional?"],
          a: "Have you thought of talking to a professional?",
          extra: ["talk", "speaking"],
          successtip: "✓ PROFESSIONAL HELP!\nTherapists, counselors, coaches! Suggesting professional help shows you care! 👨‍⚕️"
        },
        {
          type: "mc",
          q: "🎉 UNIT COMPLETE! 🎉\n\nYou've mastered giving advice with emotions! Which shows everything you learned?",
          options: [
            "I understand you're stressed. If I were you, I'd rest. Have you tried meditation?",
            "You stressed. You rest.",
            "If I was you, I rest."
          ],
          a: "I understand you're stressed. If I were you, I'd rest. Have you tried meditation?",
          successtip: "🏆✨ AMAZING WORK! ✨🏆\n\nYou can now give caring, natural advice in English! You understand emotions and know multiple ways to help friends! You're speaking like a supportive native English speaker! 🌟 Keep being awesome! 🚀💙"
        }
      ]
    }
  ],
  
  readingSet: {
    id: "m9-u13-reading",
    title: "Reading Practice - Advice Column",
    category: "reading",
    
    questions: [
      {
        type: "passage",
        passage: `**ASK EMMA - Advice Column**

**Dear Emma,**

I'm feeling extremely stressed and anxious about my new job. I started two weeks ago, and I'm worried that I'm not good enough. My coworkers seem so confident, but I feel lost and frustrated. I'm even having trouble sleeping because I'm so worried about making mistakes. What should I do?

- Overwhelmed in Seattle`
      },
      {
        type: "mc",
        q: "How is the writer feeling?",
        options: [
          "Stressed, anxious, worried, and frustrated",
          "Happy and excited",
          "Angry at coworkers"
        ],
        a: "Stressed, anxious, worried, and frustrated",
        successtip: "✓ MULTIPLE EMOTIONS!\nPeople experiencing stress often feel many negative emotions at once! 😰"
      },
      {
        type: "mc",
        q: "Why can't the writer sleep?",
        options: [
          "Worried about making mistakes",
          "The bed is uncomfortable",
          "Working night shifts"
        ],
        a: "Worried about making mistakes",
        successtip: "✓ ANXIETY SYMPTOMS!\nWorry often causes sleep problems! Common stress symptom! 😴"
      },
      {
        type: "input",
        q: "Complete: The writer has been at the new job for _____ weeks.",
        a: "two",
        alts: ["2"],
        successtip: "✓ DETAIL!\nOnly two weeks! Normal to feel unsure at a new job! ⏰"
      },
      {
        type: "passage",
        passage: `**Emma's Response:**

Dear Overwhelmed,

I completely understand how you're feeling! Starting a new job is always stressful. If I were you, I wouldn't compare myself to coworkers who have been there longer. Have you thought of asking your manager for feedback? That way, you'll know if you're actually making mistakes or just worrying unnecessarily.`
      },
      {
        type: "mc",
        q: "What does Emma say first?",
        options: [
          "She shows empathy and understanding",
          "She criticizes the writer",
          "She ignores the emotions"
        ],
        a: "She shows empathy and understanding",
        tip: "Good advice starts with empathy!",
        successtip: "✓ EMPATHY FIRST!\nI understand before advice! Perfect support structure! 💙"
      },
      {
        type: "mc",
        q: "What advice pattern does Emma use first?",
        options: [
          "If I were you, I wouldn't...",
          "You must...",
          "How about...?"
        ],
        a: "If I were you, I wouldn't...",
        successtip: "✓ NEGATIVE ADVICE!\nIf I were you, I wouldn't = advice about what NOT to do! 🚫"
      },
      {
        type: "input",
        q: "Emma suggests asking the manager for _____.",
        a: "feedback",
        tip: "Feedback = comments about performance!",
        successtip: "✓ FEEDBACK!\nAsking for feedback reduces worry! You know what's real! 📊"
      },
      {
        type: "passage",
        passage: `What about joining your coworkers for lunch? That could help you feel more comfortable. Have you tried writing down your accomplishments each day? This helps you see you're doing better than you think. Remember, everyone feels overwhelmed at first. You're doing great - give yourself time!`
      },
      {
        type: "mc",
        q: "How many additional suggestions does Emma make?",
        options: [
          "Two (joining for lunch and writing accomplishments)",
          "One",
          "Three"
        ],
        a: "Two (joining for lunch and writing accomplishments)",
        successtip: "✓ MULTIPLE SOLUTIONS!\nGood advice offers several options! Person can choose what works! 💡"
      },
      {
        type: "mc",
        q: "What pattern does Emma use: What about joining your coworkers?",
        options: [
          "What about + -ing",
          "If I were you",
          "Have you tried"
        ],
        a: "What about + -ing",
        successtip: "✓ SOCIAL SOLUTION!\nJoining coworkers helps you feel part of the team! Social integration! 👥"
      },
      {
        type: "mc",
        q: "Emma's final message is:",
        options: [
          "Reassuring and encouraging",
          "Critical and harsh",
          "Indifferent"
        ],
        a: "Reassuring and encouraging",
        successtip: "✓ COMPLETE SUPPORT!\nEmma: empathy + advice + reassurance! Perfect advice structure! 🌟"
      }
    ]
  },
  
  reviewSet: {
    id: "m9-u13-review",
    title: "Unit Review - Giving Advice with Emotions",
    category: "review",
    
    exercises: [
      {
        type: "match",
        q: "Match the emotion with its meaning:",
        pairs: [
          { left: "Anxious", right: "Nervous about something uncertain" },
          { left: "Relieved", right: "When worry goes away" },
          { left: "Frustrated", right: "Annoyed because things aren't working" },
          { left: "Grateful", right: "Thankful for something" }
        ],
        a: "completed",
        successtip: "✓ EMOTION VOCABULARY!\nYou know the nuances! These are essential for describing feelings! 🧠"
      },
      {
        type: "order",
        q: "Traduce: Si yo fuera tú, hablaría con ella",
        words: ["If", "I", "were", "you,", "I'd", "talk", "to", "her"],
        a: "If I were you, I'd talk to her",
        extra: ["was", "will"],
        successtip: "✓ PERFECT ADVICE!\nYou've mastered the If I were you pattern! 🎯"
      },
      {
        type: "mc",
        q: "Which is correct?",
        options: [
          "How about trying something new?",
          "How about to try something new?",
          "How about try something new?"
        ],
        a: "How about trying something new?",
        successtip: "✓ -ING PATTERN!\nYou remember! Always -ing after How about! ✅"
      },
      {
        type: "input",
        q: "Complete: Have you _____ meditating for stress? (past participle of try)",
        a: "tried",
        tip: "Have you tried + -ing!",
        successtip: "✓ PAST PARTICIPLE!\nYou know the pattern perfectly! Have you tried + -ing! 🧘"
      },
      {
        type: "order",
        q: "Traduce: ¿Has pensado en mudarte a otra ciudad?",
        words: ["Have", "you", "thought", "of", "moving", "to", "another", "city?"],
        a: "Have you thought of moving to another city?",
        extra: ["move", "think"],
        successtip: "✓ BIG DECISION ADVICE!\nYou can give advice for important life choices! 🏙️"
      },
      {
        type: "mc",
        q: "Your friend says: I'm so stressed! Best empathy response:",
        options: [
          "I understand. That sounds really difficult.",
          "Stop complaining.",
          "Everyone is stressed."
        ],
        a: "I understand. That sounds really difficult.",
        tip: "Show empathy first!",
        successtip: "✓ EMPATHY!\nYou know how to support friends emotionally! Perfect! 💙"
      },
      {
        type: "input",
        q: "Complete: What about _____ for a walk to clear your head? (go)",
        a: "going",
        tip: "What about + -ing!",
        successtip: "✓ STRESS RELIEF!\nGoing for a walk is great stress advice! You're giving natural suggestions! 🚶"
      },
      {
        type: "match",
        q: "Match the advice type with its strength:",
        pairs: [
          { left: "If I were you, I would", right: "Strong/Direct" },
          { left: "How about", right: "Gentle/Casual" },
          { left: "Have you tried", right: "Question/Soft" }
        ],
        a: "completed",
        successtip: "✓ ADVICE LEVELS!\nYou understand the subtle differences! Choose based on situation! 🎚️"
      },
      {
        type: "order",
        q: "Traduce: Estoy muy agradecido por tu consejo",
        words: ["I'm", "very", "grateful", "for", "your", "advice"],
        a: "I'm very grateful for your advice",
        extra: ["with", "about"],
        successtip: "✓ GRATITUDE!\nShowing thanks for good advice! You're using emotions naturally! 🙏"
      },
      {
        type: "mc",
        q: "🎉 UNIT COMPLETE! 🎉\n\nYou've learned emotions + multiple advice patterns! Which sentence combines everything perfectly?",
        options: [
          "I understand you're stressed. If I were you, I'd rest. Have you tried meditation? What about talking to someone?",
          "You're stressed. Rest now.",
          "If I was you, I rest. You try meditation."
        ],
        a: "I understand you're stressed. If I were you, I'd rest. Have you tried meditation? What about talking to someone?",
        successtip: "🏆✨ PHENOMENAL! ✨🏆\n\nYou've mastered:\n✅ Emotion vocabulary (worried, stressed, anxious, relieved, grateful!)\n✅ If I were you advice pattern\n✅ How/What about + -ing suggestions\n✅ Have you tried/thought of + -ing\n✅ Empathy + advice structure\n\nYou can now support friends in English like a native speaker! You're giving caring, thoughtful advice naturally! 🌟💙\n\nKeep spreading positivity! 🚀"
      }
    ]
  }
};
